import { NUCLEOTIDE_CODES } from "@/lib/tool-kit/biology-genetics/iupac";

/**
 * Reverse, complement and reverse-complement with IUPAC codes, preserving the case of
 * every letter (lower case often marks soft-masked repeats) and passing gap characters
 * through unchanged.
 */

export type StrandOperation = "reverseComplement" | "complement" | "reverse";
export type OutputAlphabet = "match" | "dna" | "rna";

const PAIRS: Readonly<Record<string, string>> = {
  A: "T",
  T: "A",
  U: "A",
  C: "G",
  G: "C",
  R: "Y",
  Y: "R",
  K: "M",
  M: "K",
  B: "V",
  V: "B",
  D: "H",
  H: "D",
  S: "S",
  W: "W",
  N: "N",
};

/** Complement of every upper- and lower-case code, in DNA letters. */
const CASED_COMPLEMENT: ReadonlyMap<string, string> = new Map(
  Object.entries(PAIRS).flatMap(([code, partner]) => [
    [code, partner],
    [code.toLowerCase(), partner.toLowerCase()],
  ])
);

const KNOWN = new Set(Object.keys(NUCLEOTIDE_CODES));

export function isKnownCode(character: string): boolean {
  return KNOWN.has(character.toUpperCase());
}

function complementCased(character: string): string {
  return CASED_COMPLEMENT.get(character) ?? character;
}

function toRnaLetters(sequence: string): string {
  return sequence.replaceAll("T", "U").replaceAll("t", "u");
}

function toDnaLetters(sequence: string): string {
  return sequence.replaceAll("U", "T").replaceAll("u", "t");
}

/** True when the sequence has U and no T, in either case. */
export function looksLikeRna(sequence: string): boolean {
  const upper = sequence.toUpperCase();
  return upper.includes("U") && !upper.includes("T");
}

export function convertStrand(
  sequence: string,
  operation: StrandOperation,
  alphabet: OutputAlphabet
): { sequence: string; rna: boolean } {
  const rna =
    alphabet === "match" ? looksLikeRna(sequence) : alphabet === "rna";
  const out: string[] = new Array(sequence.length);
  const last = sequence.length - 1;
  for (let i = 0; i <= last; i++) {
    const source =
      operation === "complement" ? sequence[i] : sequence[last - i];
    out[i] = operation === "reverse" ? source : complementCased(source);
  }
  const joined = out.join("");
  return { sequence: rna ? toRnaLetters(joined) : toDnaLetters(joined), rna };
}

/** Does the sequence equal its own reverse complement (a palindromic site)? */
export function isReversePalindrome(sequence: string): boolean {
  if (sequence === "") {
    return false;
  }
  const dna = toDnaLetters(sequence.toUpperCase());
  const reversed = convertStrand(dna, "reverseComplement", "dna").sequence;
  return reversed === dna;
}
